<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import { maxLength } from '@vuelidate/validators'
import type { AuthenticatedReviewIn } from '~/server/api/apps/[app_id]/review.post'
import type { RequestStatus } from '~/types/api.model'

const props = defineProps<{
  appTitle: string
  appId: string
}>()

const emit = defineEmits<{
  cancel: []
  close: []
}>()

const MAX_REVIEW_CHARACTER_LENGTH = 500

type ReviewForm = {
  rating: number | undefined
  review: string | undefined
  is_anonymous: boolean
}

const form: ReviewForm = reactive({
  rating: undefined,
  review: undefined,
  is_anonymous: false,
})

const user = useUserState()

const userName = computed(() => {
  return `${user.value?.first_name} ${user.value?.last_name}`.trim()
})

// Set `is_anonymous` automatically to `true` when there is no user name provided via the account service
watch(
  userName,
  () => {
    if (!userName.value) {
      form.is_anonymous = true
    }
  },
  { immediate: true },
)

const rules = {
  review: { maxLength: maxLength(MAX_REVIEW_CHARACTER_LENGTH) },
}

const v = useVuelidate(rules, form)

const formSubmissionStatus = ref<RequestStatus>('none')

const handleRate = (rating: number): void => {
  form.rating = rating
}

const handleIsAnonymous = (event: UiChangeEvent<HTMLInputElement>): void => {
  form.is_anonymous = event.target.checked
}

const submitReview = async (): Promise<void> => {
  const isValid = await v.value.$validate()
  if (!isValid) {
    return
  }

  try {
    formSubmissionStatus.value = 'pending'

    const requestBody: AuthenticatedReviewIn = {
      rating: form.rating as number, // Vuelidate handled the required state already
      text: form.review,
      is_anonymous: form.is_anonymous,
    }

    // Send cookie to SSR with each request
    const { authCookieName } = useAppConfig()
    const sessionId = useCookie(authCookieName).value

    await $fetch(
      `/api/apps/${props.appId}/review`,
      {
        method: 'POST',
        body: requestBody,
        headers: {
          Cookie: `${authCookieName}=${sessionId}`,
        },
        credentials: 'include',
      },
    )
    formSubmissionStatus.value = 'ok'
  } catch {
    formSubmissionStatus.value = 'error'
  }
}
</script>

<template>
  <form
    v-if="formSubmissionStatus !== 'ok'"
    class="form-embedded"
    data-test="form-embedded"
    @submit.prevent="submitReview"
  >
    <p>How many stars do you give to {{ appTitle }}?</p>
    <ProductReviewFormRatingInput
      :form="form"
      @rate="handleRate"
    />

    <div
      v-if="userName"
      class="form-embedded__user"
    >
      <span class="form-embedded__user-label">Name</span>
      <span>{{ userName }}</span>
      <UiInput
        type="checkbox"
        :checked="form.is_anonymous"
        @change="handleIsAnonymous($event as UiChangeEvent<HTMLInputElement>)"
      >
        <span class="form-embedded__checkbox-text">
          Hide name on review
        </span>
      </UiInput>
    </div>

    <div
      class="form-embedded__review"
    >
      <label class="form-embedded__label">
        Leave your review
        <span class="form-embedded__helper-text">How does it help your business? (Max {{ MAX_REVIEW_CHARACTER_LENGTH }} characters)</span>
        <textarea
          v-model="form.review"
          class="form-embedded__textarea"
        />
      </label>
      <UiFormError
        v-if="v.review.$error"
        class="mt-0"
        message="Too many characters"
        data-test="form-embedded-input-error"
      />
    </div>
    <p>
      We will use the data according to our
      <NuxtLink
        to="https://www.sendcloud.com/privacy-policy/"
        target="_blank"
      >
        privacy policy
      </NuxtLink>.
    </p>
    <UiFormError
      v-if="formSubmissionStatus === 'error'"
      data-test="form-embedded-submit-error"
      message="We encountered an error, please try again."
    />
    <ProductReviewFormButtons
      has-cancel
      @cancel="emit('cancel')"
    />
  </form>
  <div
    v-else
    class="form-embedded"
    data-test="form-embedded-thanks"
  >
    <p class="mt-0">
      Thank you for leaving a review. We will show it on the App Store shortly. You can now close this modal.
    </p>
    <UiButton
      type="primary"
      @click="emit('close')"
    >
      Close
    </UiButton>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

.form-embedded {
  display: flex;
  flex-direction: column;
  margin-top: var(--app-spacer-32);
}

.form-embedded__user-label {
  @include font-bold;

  margin-right: var(--app-spacer-4);
}

.form-embedded__checkbox-text {
  margin-left: var(--app-spacer-12);
}

.form-embedded__review {
  margin-top: var(--app-spacer-24);
  margin-bottom: var(--app-spacer-4);
}

.form-embedded__label {
  @extend %font-16-24-bold;
}

.form-embedded__helper-text {
  display: block;

  @extend %font-16-24-regular;
}

.form-embedded__textarea {
  border-radius: var(--app-border-radius-small);
  width: 100%;
  height: 120px;
  margin-top: var(--app-spacer-12);
  padding: var(--app-spacer-12);
  resize: none;
}
</style>
